import axios from "axios";
import { AnimatePresence, Variants, motion } from "framer-motion";
import { useState } from "react";
import useSWR, { mutate } from "swr";
import { useTranslations } from "next-intl";
import { TrashIcon } from "@heroicons/react/24/outline";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { useNotificationStore } from "@store/notificationStore";
import { BASE_URL, COUNTRY_CODE } from "@utils/constants";
import {
  getCookieConsent,
  getCookieValue,
  joinClassNames,
} from "@utils/helpers";
import { useLatestCookieValue, useWindowSize } from "@utils/hooks";
import { fetcher } from "@utils/fetchers";
import {
  trackGTMCartEvent,
  getAnalyticsQueryParamsFromCheckoutDetails,
} from "@utils/analyticsUtils";
import { Analytics } from "@types";
import { useCheckoutDetails } from "@features/checkout/hooks/useCheckoutDetails";
import {
  useIsShopsite,
  useRequiresCookieConsent,
} from "@context/DomainContext";
import LoadingSpinner from "@components/loadingSpinner";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/popovers/dialog";
import { analytics } from "@components/analytics";
import { Button, Card, Heading, Text } from "@components/theming";
import DialogCloseButton from "@components/popovers/dialogCloseButton";

interface Props {
  fullWidth?: boolean;
}

const DeleteCartDialog = ({ fullWidth }: Props) => {
  const windowSize = useWindowSize();
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const { setFailure, setSuccess } = useNotificationStore((state) => state);
  const translations = useTranslations("Checkout.Cart.tickets.clearcart");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const isShopsite = useIsShopsite();
  const requiresCookieConsent = useRequiresCookieConsent();
  const cookieConsent = getCookieConsent();

  const { data: checkoutDetails } = useCheckoutDetails();

  const formattedIds = getAnalyticsQueryParamsFromCheckoutDetails(
    checkoutDetails?.CartList
  );
  const { data: analyticsConfigs } = useSWR<Analytics[]>(
    formattedIds?.formattedEventIds
      ? `analytics?eventIds=${formattedIds.formattedEventIds}`
      : null,
    fetcher
  );

  const variants: Variants =
    windowSize.width && windowSize.width >= 640
      ? {
          hidden: {
            opacity: 0,
            backdropFilter: isShopsite ? "" : "blur(64px) opacity(0)",
            scale: 0.9,
            transition: { duration: 0.125 },
          },
          show: {
            opacity: 1,
            backdropFilter: isShopsite ? "" : "blur(64px) opacity(1)",
            scale: 1,
          },
        }
      : {
          hidden: {
            y: isShopsite ? 200 : "100%",
            backdropFilter: isShopsite ? "" : "blur(64px) opacity(0)",
            opacity: 0,
          },
          show: {
            y: 0,
            backdropFilter: isShopsite ? "" : "blur(64px) opacity(1)",
            opacity: 1,
          },
        };

  const handleDeleteCart = async () => {
    setLoading(true);
    const basketKey = getCookieValue("ASP.NET_SessionId");
    const websiteUid = getCookieValue("itk-website-uid");

    try {
      const response = await axios.delete(
        `${BASE_URL}/legacy/${COUNTRY_CODE}/shop/basket/${basketKey}`,
        {
          headers: {
            "itk-website-uid": websiteUid,
          },
        }
      );

      if (response.status >= 200 && response.status < 300) {
        setSuccess(translations("content.success"));
        if (
          checkoutDetails &&
          (!requiresCookieConsent || cookieConsent.marketing === true)
        ) {
          analytics.track("Product Removed", {
            quantity: 1,
            currency: checkoutDetails.Currency.CurrencyCode,
            cart_id: basketKey,
          });
          if (checkoutDetails && analyticsConfigs) {
            trackGTMCartEvent(
              checkoutDetails.CartList,
              analyticsConfigs,
              "remove_from_cart",
              checkoutDetails.Currency.CurrencyCode
            );
          }
        }
        await axios.delete("/api/basket");
        await mutate(`basket/${basketKey}`);
        await mutate(`basket/${basketKey}/summary`);
        await mutate(`basket/${basketKey}/checkout`);
        await mutate(
          (key: any) =>
            Array.isArray(key) && key[0] === `basket/${basketKey}/checkout`
        );
        await mutate(
          (key: any) => Array.isArray(key) && key[0].startsWith("events/"),
          undefined,
          {
            revalidate: true,
          }
        );
        await mutate(
          (key: any) => typeof key === "string" && key.startsWith("events/"),
          undefined,
          {
            revalidate: true,
          }
        );
      } else if (response.status >= 400 && response.status < 500) {
        setFailure(response.data.message);
      } else {
        setFailure(translations("content.failure"), true);
      }
    } catch (err) {
      console.error(err);
    }

    scroll({ top: 0, behavior: "smooth" });
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        data-testid="clearcart"
        disabled={loading || checkoutDetails?.IsPended}
        asChild
      >
        <Button
          color="red"
          size="3"
          shopsiteVariant="bordered-red"
          sharedClasses={joinClassNames(
            "max-sm:w-full",
            fullWidth ? "w-full" : ""
          )}
          shopsiteClasses="px-4 whitespace-nowrap"
        >
          {loading ? (
            <div className="flex justify-center items-center gap-2">
              <LoadingSpinner
                size="18"
                color={isShopsite ? "#ffffff" : "var(--gray-11)"}
              />
              <span>{translations("clearingcart")}</span>
            </div>
          ) : (
            <span>{translations("clearcart")}</span>
          )}
        </Button>
      </DialogTrigger>
      <AnimatePresence mode="wait">
        {open && (
          <DialogContent asChild forceMount handleClose={() => setOpen(false)}>
            <Card asChild shopsiteClasses="rounded-3xl overflow-hidden">
              <motion.div
                data-testid="clearcartcontent"
                initial="hidden"
                animate="show"
                exit="hidden"
                variants={variants}
                transition={{ duration: 0.2 }}
              >
                <div className="flex flex-col">
                  <div
                    className={joinClassNames(
                      "flex flex-col gap-4 pb-4",
                      isShopsite ? "px-6 pt-6 gap-4 bg-red-500" : ""
                    )}
                  >
                    <div className="relative self-end">
                      <DialogClose asChild>
                        <DialogCloseButton shopsiteVariant="white" />
                      </DialogClose>
                    </div>
                    <DialogHeader className="!text-left">
                      <DialogTitle asChild>
                        <Heading
                          as="h3"
                          size="5"
                          weight="bold"
                          shopsiteClasses="text-white text-xl font-bold"
                        >
                          {translations("content.heading")}
                        </Heading>
                      </DialogTitle>
                      <TrashIcon
                        className={joinClassNames(
                          "absolute top-0 left-4 w-24 h-24 -rotate-12",
                          isShopsite
                            ? "text-white/10"
                            : " text-[color:var(--red-a3)]"
                        )}
                      />
                    </DialogHeader>
                  </div>
                  <Text color="gray" shopsiteClasses="text-gray-600 px-6 pt-4">
                    {translations("content.description")}
                  </Text>
                  <div
                    className={joinClassNames(
                      "flex-1 flex items-center justify-between gap-6 mt-4",
                      isShopsite ? "px-6 pb-6" : ""
                    )}
                  >
                    <DialogClose asChild>
                      <Button
                        shopsiteClasses="!bg-gray-100 hover:!bg-gray-500 !text-gray-800 hover:!text-white"
                        color="gray"
                        size="3"
                        sharedClasses="grow"
                      >
                        {translations("content.cancel")}
                      </Button>
                    </DialogClose>
                    <DialogClose
                      data-testid="deletecart"
                      asChild
                      onClick={handleDeleteCart}
                    >
                      <Button
                        color="red"
                        size="3"
                        sharedClasses="grow"
                        shopsiteClasses="bg-red-500 hover:bg-red-400 text-white"
                      >
                        {translations("content.delete")}
                      </Button>
                    </DialogClose>
                  </div>
                </div>
              </motion.div>
            </Card>
          </DialogContent>
        )}
      </AnimatePresence>
    </Dialog>
  );
};

export default DeleteCartDialog;
